import React, {useState, forwardRef} from "react";
import Popover from "@mui/material/Popover";
import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../pages/HomePage/HomePage";
import CustomCurrencyInput from "./CustomCurrencyInput";
import classnames from "classnames";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import {NumericFormat} from 'react-number-format';
import {costFromInput, costUpdate, deriveFromCost, hasBase} from "./costCalculatorMath";

// Inline calculator SVG (matches reference). forwardRef so MUI Tooltip can
// attach a ref to the trigger.
const CalcIcon = forwardRef(function CalcIcon({ active, disabled, neutral, onClick, ...rest }, ref) {
    return (
        <svg
            ref={ref}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            onClick={onClick}
            style={{
                width: 15,
                height: 15,
                verticalAlign: 'middle',
                cursor: disabled ? 'default' : 'pointer',
                // Blue (matches the Calculator.html reference cost-icon), via
                // the page's --accent-blue token so it tracks light/dark.
                // `neutral` is the read-only historical view: black, not a link.
                color: neutral ? 'var(--text-primary)' : 'var(--accent-blue)',
                marginLeft: 4,
            }}
            {...rest}
        >
            <rect x="4" y="2" width="16" height="20" rx="2"/>
            <line x1="8" y1="6" x2="16" y2="6"/>
            <line x1="8" y1="10" x2="8" y2="10" strokeWidth="3"/>
            <line x1="12" y1="10" x2="12" y2="10" strokeWidth="3"/>
            <line x1="16" y1="10" x2="16" y2="10" strokeWidth="3"/>
            <line x1="8" y1="14" x2="8" y2="14" strokeWidth="3"/>
            <line x1="12" y1="14" x2="12" y2="14" strokeWidth="3"/>
            <line x1="16" y1="14" x2="16" y2="14" strokeWidth="3"/>
            <line x1="8" y1="18" x2="8" y2="18" strokeWidth="3"/>
            <line x1="12" y1="18" x2="12" y2="18" strokeWidth="3"/>
            <line x1="16" y1="18" x2="16" y2="18" strokeWidth="3"/>
        </svg>
    )
})

// Larger version of the same icon for the popover header.
const CalcHeaderIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="16" height="20" rx="2"/>
        <line x1="8" y1="6" x2="16" y2="6"/>
        <line x1="8" y1="10" x2="8" y2="10" strokeWidth="3"/>
        <line x1="12" y1="10" x2="12" y2="10" strokeWidth="3"/>
        <line x1="16" y1="10" x2="16" y2="10" strokeWidth="3"/>
        <line x1="8" y1="14" x2="8" y2="14" strokeWidth="3"/>
        <line x1="12" y1="14" x2="12" y2="14" strokeWidth="3"/>
        <line x1="16" y1="14" x2="16" y2="14" strokeWidth="3"/>
        <line x1="8" y1="18" x2="8" y2="18" strokeWidth="3"/>
        <line x1="12" y1="18" x2="12" y2="18" strokeWidth="3"/>
        <line x1="16" y1="18" x2="16" y2="18" strokeWidth="3"/>
    </svg>
)

const DisplayRow = ({ label, value }) => (
    <div className="cc-row">
        <span className="cell-label">{label}</span>
        <div className="cell-display">
            <span className="prefix">$</span>
            <span className="value">
                <NumericFormat
                    value={value || 0}
                    displayType="text"
                    thousandSeparator
                />
            </span>
        </div>
    </div>
)

// allowNegative={false}: the +/- label already carries the direction, so a typed
// sign could only contradict it. Matches the Price Adjust magnitude control.
const CurrencyCell = ({ value, onChange, onBlur, disabled, id }) => (
    <div className={classnames('cell-input', {'is-disabled': disabled})}>
        <span className="prefix">$</span>
        <CustomCurrencyInput
            id={id}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            allowNegative={false}
            allowBlank={true}
            InputProps={{}}
        />
    </div>
)

const PercentCell = ({ value, onChange, onBlur, disabled, id }) => (
    <div className={classnames('cell-pct', {'is-disabled': disabled})}>
        <CustomCurrencyInput
            id={id}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            allowNegative={false}
            allowBlank={true}
            InputProps={{}}
        />
        <span className="suffix">%</span>
    </div>
)

export const CostCalculator = (props) => {
    const {
        state,
        setState,
        handleOnFocus,
        handleBlur,
        // Read-only historical deal: the trigger renders black and inert, and
        // the calculator popover never opens.
        disabled: isReadOnly
    } = props
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const [anchorEl, setAnchorEl] = useState(null);

    // The field being typed in shows the user's raw keystrokes; every other one
    // shows a value derived from Cost. Without the split, typing "5" over a
    // displayed "4.21" would be overwritten by the re-derived "4.21" on the next
    // render. Cleared on blur, when the field rejoins the derived view.
    const [activeField, setActiveField] = useState(null)
    const [rawInput, setRawInput] = useState('')

    // Derived fresh from the only three real values. The persisted *_plus /
    // *_minus / *_percentage columns are output, not input, so a stale one can
    // never drive the display or reach back into Cost.
    const derived = deriveFromCost(state.msrp, state.invoice, state.cost)

    const cellValue = (field) => (
        activeField === field ? rawInput : (derived[field] ?? '')
    )

    const handleActionClick = (event) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget);
    }

    const handleActionClose = () => {
        setAnchorEl(null);
    }

    // The single writer: one edit -> one new Cost -> every field re-derived from
    // it on the next render. No field writes to another, so there is no cycle.
    const handleFieldChange = (field, value) => {
        setActiveField(field)
        setRawInput(value)

        const newCost = costFromInput(field, value, state.msrp, state.invoice)
        // Clearing a box leaves the committed Cost alone.
        if (newCost === null) return

        setState({...state, ...costUpdate(state.msrp, state.invoice, newCost)})
    }

    const handleFieldBlur = () => {
        setActiveField(null)
        setRawInput('')
    }

    const isInvoiceDisabled = !hasBase(state.invoice)
    const isMsrpDisabled = !hasBase(state.msrp)
    const isTriggerDisabled = isReadOnly || isMsrpDisabled

    return (
        <React.Fragment>
            {/* An empty title renders no tooltip, so read-only loses the
                "Cost Calculator" hint along with the handler. */}
            <Tooltip title={isReadOnly ? '' : 'Cost Calculator'} arrow placement={'top'}>
                <CalcIcon
                    disabled={isTriggerDisabled}
                    neutral={isReadOnly}
                    onClick={isTriggerDisabled ? undefined : handleActionClick}
                    onFocus={(e) => handleOnFocus && handleOnFocus(e)}
                    onBlur={(e) => handleBlur && handleBlur(e)}
                />
            </Tooltip>
            <Popover
                onClick={(e) => e.stopPropagation()}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleActionClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Box className={classnames('CalcPageInvoiceCalculator', {
                    mobile: isTabletOrMobile
                })}>
                    <div className="cc-card">
                        <div className="cc-card-header">
                            <CalcHeaderIcon/>
                            <h2>Cost Calculator</h2>
                        </div>

                        <div className="cc-card-body">
                            {/* ── MSRP ── */}
                            <DisplayRow label="MSRP" value={state.msrp}/>

                            {/* MSRP adjust: − $ Or, + $ */}
                            <div className="cc-row">
                                <span className="cell-op">−</span>
                                <CurrencyCell
                                    id="cost-calculator-msrp_minus"
                                    value={cellValue('msrp_minus')}
                                    onChange={(v) => handleFieldChange('msrp_minus', v)}
                                    onBlur={handleFieldBlur}
                                    disabled={isMsrpDisabled}
                                />
                                <span className="cell-or">Or,</span>
                                <span className="cell-op">+</span>
                                <CurrencyCell
                                    id="cost-calculator-msrp_plus"
                                    value={cellValue('msrp_plus')}
                                    onChange={(v) => handleFieldChange('msrp_plus', v)}
                                    onBlur={handleFieldBlur}
                                    disabled={isMsrpDisabled}
                                />
                            </div>

                            {/* MSRP percentage: spacer Or, − % */}
                            <div className="cc-row">
                                <div className="cell-spacer"/>
                                <span className="cell-or">Or,</span>
                                <span className="cell-op">−</span>
                                <PercentCell
                                    id="cost-calculator-msrp_percentage"
                                    value={cellValue('msrp_percentage')}
                                    onChange={(v) => handleFieldChange('msrp_percentage', v)}
                                    onBlur={handleFieldBlur}
                                    disabled={isMsrpDisabled}
                                />
                            </div>

                            <div className="cc-divider"/>

                            {/* ── Invoice ── */}
                            <DisplayRow label="Invoice" value={state.invoice}/>

                            {/* Invoice adjust: − $ Or, + $ */}
                            <div className="cc-row">
                                <span className="cell-op">−</span>
                                <CurrencyCell
                                    id="cost-calculator-invoice_minus"
                                    value={cellValue('invoice_minus')}
                                    onChange={(v) => handleFieldChange('invoice_minus', v)}
                                    onBlur={handleFieldBlur}
                                    disabled={isInvoiceDisabled}
                                />
                                <span className="cell-or">Or,</span>
                                <span className="cell-op">+</span>
                                <CurrencyCell
                                    id="cost-calculator-invoice_plus"
                                    value={cellValue('invoice_plus')}
                                    onChange={(v) => handleFieldChange('invoice_plus', v)}
                                    onBlur={handleFieldBlur}
                                    disabled={isInvoiceDisabled}
                                />
                            </div>

                            <div className="cc-divider"/>

                            {/* ── Cost ── */}
                            <DisplayRow label="Cost" value={state.cost}/>
                        </div>
                    </div>
                </Box>
            </Popover>
        </React.Fragment>
    )
}
